import { useMemo } from 'react';

import { UseTableFiltersReturn } from '@/components/data-table/filters-v2';

interface UnsavedChangesGuardLike {
  checkUnsavedChanges: (onProceed: () => void) => boolean;
}

/**
 * Filter mutators are typically called from inside an open Radix popover
 * (filter chip dropdown, sub-menu, select). When the guard intercepts the
 * mutation and shows the confirm modal instead, that popover stays open
 * underneath the modal. Dispatch Escape so every floating Radix layer
 * (Popover, DropdownMenu, Select, etc.) closes itself — they all listen
 * for the same key.
 */
export const dismissFloatingLayers = () => {
  if (typeof document === 'undefined') return;
  document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
};

/**
 * Wraps a v2 `useTableFilters` return value so every mutation goes through an
 * unsaved-changes guard. When the form is dirty, the user sees the confirm
 * modal first; on discard the original mutation runs.
 *
 * Read-side fields (`activeFilters`, `getFilterValue`, etc.) are passed through
 * untouched — only the mutators are intercepted.
 */
export const useGuardedFilterState = (
  filterState: UseTableFiltersReturn,
  guard: UnsavedChangesGuardLike
): UseTableFiltersReturn => {
  return useMemo<UseTableFiltersReturn>(() => {
    const guarded =
      <Args extends unknown[]>(action: (...args: Args) => void) =>
      (...args: Args) => {
        if (guard.checkUnsavedChanges(() => action(...args))) {
          action(...args);
        } else {
          dismissFloatingLayers();
        }
      };

    return {
      ...filterState,
      setFilter: guarded(filterState.setFilter),
      clearFilter: guarded(filterState.clearFilter),
      clearAllFilters: guarded(filterState.clearAllFilters),
      applyFilters: guarded(filterState.applyFilters),
      removeFilterValue: guarded(filterState.removeFilterValue),
    };
  }, [filterState, guard]);
};
